import { useMutation, useQuery } from "@tanstack/solid-query";
import {
  type OverviewResponse,
  OverviewResponseSchema,
} from "contracts/api/dashboard/responses";
import type { IssueWriteRequest } from "contracts/api/issue/requests";
import type { Issue } from "contracts/api/issue/responses";
import type { UpdateScanScheduleRequest } from "contracts/api/website/requests";
import type { Accessor } from "solid-js";
import { api } from "../../store/api";
import { type IssueStatusInput, queryClient } from "../../store/query";

// One store per website, shared by every surface. TanStack Query dedupes the
// fetch across mounted views, renders from the persisted snapshot instantly,
// and revalidates in the background. Issue-status writes are offline-first
// mutations (optimistic via the "issue-status" defaults in store/query.ts);
// the other writes are server-confirmed and land in the cache on response.
export type WebsiteContext = {
  websiteId: string;
  data: Accessor<OverviewResponse | null>;
  setIssueStatus: (issue: Issue, status: Issue["status"]) => void;
  saveIssue: (
    issueId: string | null,
    input: IssueWriteRequest,
  ) => Promise<Issue>;
  archiveIssue: (issue: Issue) => Promise<Issue>;
  setScanSchedule: (input: UpdateScanScheduleRequest) => Promise<void>;
  clearScanSchedule: () => Promise<void>;
};

function overviewQueryKey(websiteId: string): [string, string] {
  return ["overview", websiteId];
}

export function createWebsiteStore(websiteId: string): WebsiteContext {
  const query = useQuery(() => ({
    queryKey: overviewQueryKey(websiteId),
    queryFn: async () =>
      OverviewResponseSchema.parse(await api.overview(websiteId)),
  }));
  const issueStatus = useMutation<void, Error, IssueStatusInput>(() => ({
    mutationKey: ["issue-status"],
  }));

  const patch = (mutate: (current: OverviewResponse) => OverviewResponse) => {
    queryClient.setQueryData<OverviewResponse>(
      overviewQueryKey(websiteId),
      (current) => current && mutate(current),
    );
  };

  return {
    websiteId,
    data: () => query.data ?? null,
    setIssueStatus(issue, status) {
      issueStatus.mutate({ websiteId, issueId: issue.id, status });
    },
    async saveIssue(issueId, input) {
      const saved = issueId
        ? await api.updateIssue(issueId, input)
        : await api.createIssue(websiteId, input);
      patch((current) => {
        const exists = current.issues.some((issue) => issue.id === saved.id);
        const issues = exists
          ? current.issues.map((issue) =>
              issue.id === saved.id ? saved : issue,
            )
          : [...current.issues, saved];
        return { ...current, issues };
      });
      return saved;
    },
    async archiveIssue(issue) {
      const saved = await api.archiveIssue(issue.id);
      patch((current) => ({
        ...current,
        issues: current.issues.map((row) =>
          row.id === saved.id ? saved : row,
        ),
      }));
      return saved;
    },
    async setScanSchedule(input) {
      const scanSchedule = await api.setScanSchedule(websiteId, input);
      patch((current) => ({ ...current, scanSchedule }));
    },
    async clearScanSchedule() {
      const scanSchedule = await api.clearScanSchedule(websiteId);
      patch((current) => ({ ...current, scanSchedule }));
    },
  };
}
